import type { ChangeEvent } from 'react';
import { useShallow } from 'zustand/shallow';

import { useSignaturesIndexStore } from '@/store/docusign-modal.store';
import Checkbox from '@ingka/checkbox';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import Text from '@ingka/text';
import { translate } from '@repo/phrase';
import { UploadAttachment } from '@repo/ui/attachments';
import { getNotifier } from '@repo/utils';

import { LoadingPage } from '@/components/loading';

import { useResponsive } from '@/hooks/responsive.hooks';
import { getAppConfig } from '@/utils/window.utils';

import { AppendixViewContent } from '../appendix-view/appendix-view.component';
import { useRequestTemplates } from './signatures-request-view.hooks';

import styles from './styles.module.scss';

interface Props {
    loading?: boolean;
}

export const SignatureRequestView = ({ loading }: Props) => {
    const { email, setEmail, selectedRequestTemplates, setSelectedRequestTemplates, appendixFiles, setAppendixFiles } =
        useSignaturesIndexStore(
            useShallow((state) => ({
                email: state.email,
                setEmail: state.setEmail,
                selectedRequestTemplates: state.selectedRequestTemplates,
                setSelectedRequestTemplates: state.setSelectedRequestTemplates,
                appendixFiles: state.appendixFiles,
                setAppendixFiles: state.setAppendixFiles,
            }))
        );

    const { templates, isLoading, isEmpty, hasError } = useRequestTemplates();
    const { isDesktop } = useResponsive();
    const config = getAppConfig();
    const acceptedFileTypes = config.feature.docusign?.acceptedFileTypes ?? [];

    const disabled = Boolean(loading);

    const handleAppendixFileSelect = (file: File) => {
        if (!acceptedFileTypes.some((fileType) => file.name.toLowerCase().endsWith(fileType.toLowerCase()))) {
            getNotifier()?.add({
                title: translate('docusign_appendix_invalid_file_type', {
                    fileTypes: acceptedFileTypes.map((fileType) => fileType.replace('.', '').toUpperCase()).join(','),
                }),
                type: 'error',
            });
            return;
        }

        const currentFiles = useSignaturesIndexStore.getState().appendixFiles;
        setAppendixFiles([...currentFiles, { id: crypto.randomUUID(), file }]);
    };

    const handleTemplateSelectionChange = (templateId: string, checked: boolean) => {
        const nextSelectedRequestTemplates = checked
            ? [...selectedRequestTemplates, templateId]
            : selectedRequestTemplates.filter((type) => type !== templateId);

        if (appendixFiles.length > 0) {
            setAppendixFiles([]);
        }

        setSelectedRequestTemplates(nextSelectedRequestTemplates);
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    if (hasError) {
        return (
            <div data-testid="request-templates-error">
                <Text>{translate('docusign_templates_fetch_error')}</Text>
            </div>
        );
    }

    if (isEmpty) {
        return (
            <div data-testid="request-templates-empty">
                <Text>{translate('docusign_no_templates_available')}</Text>
            </div>
        );
    }

    return (
        <div data-testid="signatures-request-mode" className={styles.grid}>
            <div className={styles.field}>
                <Text data-testid="signature-request-email-label" className={String(styles.fieldLabelText)}>
                    {translate('docusign_email')}
                </Text>
                <InputField
                    id="signature-request-email"
                    type="email"
                    value={email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                        setEmail(e.target.value);
                    }}
                    disabled={disabled}
                    data-testid="signature-request-email"
                />
            </div>

            <div className={styles.field}>
                <Text className={String(styles.fieldLabelText)}>{translate('orderview_signatures_request_for')}</Text>
                <div className={styles.checks}>
                    {templates
                        .filter(({ templateId }) => templateId !== undefined)
                        .map((template) => {
                            return (
                                <FormField key={`${template.templateId}`}>
                                    <Checkbox
                                        id={`${template.templateId}`}
                                        value={`${template.templateId}`}
                                        label={`${template.templateName ?? template.templateId}`}
                                        checked={selectedRequestTemplates.includes(`${template.templateId}`)}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                            handleTemplateSelectionChange(`${template.templateId}`, e.target.checked);
                                        }}
                                    />
                                </FormField>
                            );
                        })}
                </div>
            </div>

            {appendixFiles.length > 0 && (
                <div className={styles.field}>
                    <Text className={String(styles.fieldLabelText)}>{translate('docusign_appendices_title')}</Text>
                    <AppendixViewContent
                        rows={appendixFiles}
                        onDelete={(id) => {
                            setAppendixFiles(appendixFiles.filter((row) => row.id !== id));
                        }}
                    />
                </div>
            )}

            {/* Appendix upload is only available when a single template is selected */}
            {selectedRequestTemplates.length === 1 && (
                <div className={styles.field}>
                    <UploadAttachment
                        onFileSelect={handleAppendixFileSelect}
                        isDesktop={isDesktop}
                        accept={acceptedFileTypes.join(',')}
                        translate={translate}
                        translations={{
                            header: translate('docusign_header_upload_appendix'),
                            uploadAction: translate('docusign_upload_appendix'),
                        }}
                    />
                </div>
            )}
        </div>
    );
};
